import { useEffect, useMemo, useState } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Spinner } from '@/components/ui/spinner'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import type { Condition } from '@/lib/expr'
import { platformApi } from '../api'
import ConditionEditor, { type ConditionField } from '../components/ConditionEditor'
import type { Form } from '../types'
import { dashboardApi } from './api'
import {
    BUCKETS, MAX_COLUMNS, MAX_LIMIT, PERIODS, STATUS_OPTIONS, WIDGET_TYPES, cleanSpec, dateFields, defaultTitle, fieldsOf,
    groupableFields, newWidgetId, numberFields, specHints,
    type Bucket, type Period, type FieldMeta, type GroupKind, type MetricOp, type SortMode, type Widget, type WidgetResult, type WidgetType,
} from './model'
import { ErrorState, WidgetTile } from './WidgetTile'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Widget being edited; null creates a new one. */
    initial: Widget | null
    forms: Form[]
    onSave: (w: Widget) => void
}

const METRICS: { value: MetricOp; label: string }[] = [
    { value: 'count', label: 'Count of requests' },
    { value: 'percent', label: 'Share of requests with status…' },
    { value: 'sum', label: 'Sum' },
    { value: 'avg', label: 'Average' },
    { value: 'min', label: 'Minimum' },
    { value: 'max', label: 'Maximum' },
]
const SORTS: { value: SortMode; label: string }[] = [
    { value: 'value_desc', label: 'Largest first' },
    { value: 'value_asc', label: 'Smallest first' },
    { value: 'label', label: 'Alphabetical' },
]

function blank(forms: Form[]): Widget {
    return { id: newWidgetId(), title: '', type: 'kpi', form: forms[0]?.slug ?? '' }
}

const ALL_FORMS = '__all'
const DASH_PERIOD = '__dashboard'
const SEVERITY_OPTIONS = [
    { value: 'high', label: 'High' }, { value: 'medium', label: 'Medium' }, { value: 'low', label: 'Low' }, { value: 'info', label: 'Info' },
]

function Pick<T extends string>({ id, value, onChange, options, placeholder, label }: {
    id?: string; value: T | undefined; onChange: (v: T) => void; options: { value: T; label: string }[]; placeholder?: string; label: string
}) {
    return (
        <Select value={value || undefined} onValueChange={(v) => onChange(v as T)}>
            <SelectTrigger id={id} className="w-full" aria-label={label}><SelectValue placeholder={placeholder} /></SelectTrigger>
            <SelectContent><SelectGroup>
                {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectGroup></SelectContent>
        </Select>
    )
}

export default function WidgetEditor({ open, onOpenChange, initial, forms, onSave }: Props) {
    const [w, setW] = useState<Widget>(() => initial ?? blank(forms))
    const [fields, setFields] = useState<FieldMeta[]>([])
    const [defLoading, setDefLoading] = useState(false)
    const [defError, setDefError] = useState('')
    const [preview, setPreview] = useState<{ result?: WidgetResult; error?: string; problems?: string[]; loading: boolean }>({ loading: false })

    // Reset whenever the sheet is (re)opened.
    useEffect(() => {
        if (open) { setW(initial ?? blank(forms)); setPreview({ loading: false }) }
    }, [open, initial, forms])

    const form = forms.find((f) => f.slug === w.form || f.id === w.form)

    // Fields come from the chosen form's ACTIVE definition.
    useEffect(() => {
        if (!open || !w.form) { setFields([]); return }
        let live = true
        setDefLoading(true); setDefError('')
        platformApi.getForm(w.form)
            .then((r) => { if (live) setFields(fieldsOf(r.version?.definition)) })
            .catch((e) => { if (live) { setFields([]); setDefError((e as Error).message) } })
            .finally(() => { if (live) setDefLoading(false) })
        return () => { live = false }
    }, [open, w.form])

    const patch = (p: Partial<Widget>) => setW((cur) => ({ ...cur, ...p }))
    const hints = useMemo(() => specHints(w, fields), [w, fields])
    const spec = useMemo(() => cleanSpec(w, fields, form?.name?.toLowerCase()), [w, fields, form])
    const specKey = JSON.stringify(spec)
    const ready = hints.length === 0 && !defLoading

    // Debounced live preview of the unsaved spec.
    useEffect(() => {
        if (!open || !ready) { setPreview({ loading: false }); return }
        let live = true
        setPreview((p) => ({ ...p, loading: true }))
        const t = setTimeout(() => {
            dashboardApi.previewWidget(JSON.parse(specKey) as Widget)
                .then((result) => live && setPreview({ result, loading: false }))
                .catch((e: { message?: string; problems?: string[] }) => live && setPreview({ error: e.message ?? 'Preview failed', problems: e.problems, loading: false }))
        }, 500)
        return () => { live = false; clearTimeout(t) }
    }, [open, ready, specKey])

    const groupable = groupableFields(fields, w.type)
    const isChart = w.type === 'bar' || w.type === 'pie' || w.type === 'line'
    const g = w.groupBy
    const keyIsDate = g?.kind === 'field' && dateFields(fields).some((f) => f.key === g.key)
    const showBucket = g?.kind === 'date' || keyIsDate

    const noForm = !w.form
    const condFields: ConditionField[] = [
        { key: '$status', label: 'Status', type: 'status', options: STATUS_OPTIONS },
        { key: '$form', label: 'Form', type: 'single_select', options: forms.map((f) => ({ value: f.slug, label: f.name })) },
        { key: '$department', label: 'Business unit', type: 'text' },
        { key: '$finding_severity', label: 'Has a finding of severity', type: 'single_select', options: SEVERITY_OPTIONS },
        ...(noForm ? [] : fields).map((f) => ({ key: f.key, label: f.label, type: f.type, options: f.options })),
    ]
    const groupKinds: { value: GroupKind; label: string }[] = w.type === 'line'
        ? [{ value: 'date', label: 'Submission date' }, ...(noForm ? [] : [{ value: 'field' as GroupKind, label: 'A date question' }])]
        : [
            ...(noForm ? [] : [{ value: 'field' as GroupKind, label: 'A question' }]),
            { value: 'status', label: 'Status' }, { value: 'severity', label: 'Severity' },
            { value: 'form', label: 'Form' }, { value: 'category', label: 'Category' }, { value: 'department', label: 'Business unit' },
            { value: 'date', label: 'Submission date' },
        ]

    const setType = (type: WidgetType) => {
        const next: Partial<Widget> = { type }
        if (type === 'kpi' || type === 'table') next.groupBy = undefined
        else if (!w.groupBy || (type === 'line' && w.groupBy.kind !== 'date' && w.groupBy.kind !== 'field')) next.groupBy = { kind: 'date', bucket: 'month' }
        if (type === 'line' && w.groupBy?.kind === 'field' && !dateFields(fields).some((f) => f.key === w.groupBy?.key)) next.groupBy = { kind: 'date', bucket: 'month' }
        if (type === 'table') next.metric = undefined
        patch(next)
    }
    const setKind = (kind: GroupKind) => patch({ groupBy: { kind, ...(kind === 'date' ? { bucket: g?.bucket ?? 'month' } : {}) } })
    const op = w.metric?.op ?? 'count'
    const cols = w.columns ?? []

    const save = () => onSave({ ...spec })

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full gap-0 data-[side=right]:sm:max-w-5xl">
                <SheetHeader>
                    <SheetTitle>{initial ? 'Edit widget' : 'Add widget'}</SheetTitle>
                    <SheetDescription>Choose a form and what to measure. The preview updates as you go.</SheetDescription>
                </SheetHeader>
                <div className="grid min-h-0 flex-1 gap-6 overflow-y-auto px-4 pb-4 lg:grid-cols-2">
                    <FieldGroup>
                        <Field>
                            <FieldLabel htmlFor="w-title">Title</FieldLabel>
                            <Input id="w-title" value={w.title} placeholder={defaultTitle(w, fields, form?.name?.toLowerCase())} onChange={(e) => patch({ title: e.target.value })} />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="w-form">Form</FieldLabel>
                            <Pick id="w-form" label="Form" value={w.form || ALL_FORMS} placeholder="Choose a form…"
                                options={[{ value: ALL_FORMS, label: 'All forms' }, ...forms.map((f) => ({ value: f.slug, label: f.name }))]}
                                onChange={(slug) => patch({
                                    form: slug === ALL_FORMS ? '' : slug, filters: undefined, columns: undefined,
                                    metric: w.metric?.op === 'percent' ? w.metric : undefined,
                                    groupBy: isChart ? { kind: 'date', bucket: 'month' } : undefined,
                                })} />
                            {defLoading && <FieldDescription className="flex items-center gap-2"><Spinner /> Loading questions…</FieldDescription>}
                            {defError && <FieldDescription className="text-destructive">{defError}</FieldDescription>}
                        </Field>
                        <FieldSet>
                            <FieldLegend variant="label">Visualisation</FieldLegend>
                            <ToggleGroup type="single" variant="outline" value={w.type} className="flex-wrap" aria-label="Widget type"
                                onValueChange={(v) => v && setType(v as WidgetType)}>
                                {WIDGET_TYPES.map((t) => <ToggleGroupItem key={t.value} value={t.value}>{t.label}</ToggleGroupItem>)}
                            </ToggleGroup>
                        </FieldSet>

                        {isChart && (
                            <FieldSet>
                                <FieldLegend variant="label">Group by</FieldLegend>
                                <FieldGroup>
                                    <Field>
                                        <Pick label="Group by" value={g?.kind} options={groupKinds} placeholder="Choose…" onChange={setKind} />
                                    </Field>
                                    {g?.kind === 'field' && (
                                        <Field>
                                            <FieldLabel htmlFor="w-key">Question</FieldLabel>
                                            <Pick id="w-key" label="Question to group by" value={g.key} placeholder="Choose a question…"
                                                options={groupable.map((f) => ({ value: f.key, label: f.label }))}
                                                onChange={(key) => patch({ groupBy: { kind: 'field', key } })} />
                                            {groupable.length === 0 && !defLoading && <FieldDescription>This form has no groupable questions.</FieldDescription>}
                                        </Field>
                                    )}
                                    {showBucket && (
                                        <Field>
                                            <FieldLabel htmlFor="w-bucket">Bucket</FieldLabel>
                                            <Pick id="w-bucket" label="Date bucket" value={g?.bucket ?? 'month'}
                                                options={BUCKETS.map((b) => ({ value: b, label: b[0].toUpperCase() + b.slice(1) }))}
                                                onChange={(b: Bucket) => patch({ groupBy: { ...g!, bucket: b } })} />
                                            <FieldDescription>Dates are grouped in UTC.</FieldDescription>
                                        </Field>
                                    )}
                                    {g?.kind === 'severity' && <FieldDescription>Counts distinct submissions with a finding at each severity.</FieldDescription>}
                                </FieldGroup>
                            </FieldSet>
                        )}

                        {w.type !== 'table' && g?.kind !== 'severity' && (
                            <FieldSet>
                                <FieldLegend variant="label">Measure</FieldLegend>
                                <FieldGroup>
                                    <Field>
                                        <Pick label="Metric" value={op} options={METRICS.filter((m) => !noForm || m.value === 'count' || m.value === 'percent')}
                                            onChange={(o) => patch({ metric: o === 'count' ? undefined : o === 'percent' ? { op: o, of: w.metric?.of ?? { statuses: [] } } : { op: o, field: w.metric?.field } })} />
                                    </Field>
                                    {op === 'percent' && (
                                        <Field>
                                            <FieldLabel>Statuses counted in the share</FieldLabel>
                                            <ToggleGroup type="multiple" variant="outline" size="sm" className="flex-wrap" aria-label="Statuses counted in the share"
                                                value={w.metric?.of?.statuses ?? []} onValueChange={(v) => patch({ metric: { op: 'percent', of: { statuses: v } } })}>
                                                {STATUS_OPTIONS.map((s) => <ToggleGroupItem key={s.value} value={s.value}>{s.label}</ToggleGroupItem>)}
                                            </ToggleGroup>
                                            <FieldDescription>Percent of matching requests whose status is one of these.</FieldDescription>
                                        </Field>
                                    )}
                                    {op !== 'count' && op !== 'percent' && (
                                        <Field>
                                            <FieldLabel htmlFor="w-metric-field">Number question</FieldLabel>
                                            <Pick id="w-metric-field" label="Number question" value={w.metric?.field} placeholder="Choose a number question…"
                                                options={numberFields(fields).map((f) => ({ value: f.key, label: f.label }))}
                                                onChange={(field) => patch({ metric: { op, field } })} />
                                            {numberFields(fields).length === 0 && !defLoading && <FieldDescription>This form has no number questions.</FieldDescription>}
                                        </Field>
                                    )}
                                </FieldGroup>
                            </FieldSet>
                        )}

                        {w.type === 'table' && noForm && (
                            <FieldDescription>Question columns are available when a single form is chosen.</FieldDescription>
                        )}
                        {w.type === 'table' && !noForm && (
                            <FieldSet>
                                <FieldLegend variant="label">Columns ({cols.length}/{MAX_COLUMNS} questions)</FieldLegend>
                                <FieldDescription>Number, title, submitter, status and date are always shown.</FieldDescription>
                                <div className="flex max-h-48 flex-col gap-2 overflow-y-auto rounded-lg border p-3">
                                    {fields.length === 0 && <span className="text-sm text-muted-foreground">Choose a form first.</span>}
                                    {fields.map((f) => {
                                        const on = cols.includes(f.key)
                                        return (
                                            <Field key={f.key} orientation="horizontal">
                                                <Checkbox id={`col-${f.key}`} checked={on} disabled={!on && cols.length >= MAX_COLUMNS}
                                                    onCheckedChange={(c) => patch({ columns: c ? [...cols, f.key] : cols.filter((k) => k !== f.key) })} />
                                                <FieldLabel htmlFor={`col-${f.key}`} className="font-normal">{f.label}</FieldLabel>
                                            </Field>
                                        )
                                    })}
                                </div>
                            </FieldSet>
                        )}

                        <Field>
                            <FieldLabel htmlFor="w-period">Period</FieldLabel>
                            <Pick id="w-period" label="Period" value={w.dateRange?.preset ?? DASH_PERIOD}
                                options={[{ value: DASH_PERIOD, label: 'Use the dashboard period' }, ...PERIODS]}
                                onChange={(v) => patch({ dateRange: v === DASH_PERIOD ? undefined : { preset: v as Period } })} />
                            <FieldDescription>A fixed period is replaced when a viewer picks a dashboard period.</FieldDescription>
                        </Field>

                        <Separator />

                        <FieldSet>
                            <FieldLegend variant="label">Filters</FieldLegend>
                            <ConditionEditor value={w.filters} fields={condFields} emptyText="No filters: all matching submissions count."
                                onChange={(c: Condition | undefined) => patch({ filters: c })} />
                        </FieldSet>

                        <FieldSet>
                            <FieldLegend variant="label">Statuses</FieldLegend>
                            <FieldDescription>Leave empty to count everything except drafts.</FieldDescription>
                            <ToggleGroup type="multiple" variant="outline" size="sm" className="flex-wrap" aria-label="Statuses to include"
                                value={w.statuses ?? []} onValueChange={(v) => patch({ statuses: v })}>
                                {STATUS_OPTIONS.map((s) => <ToggleGroupItem key={s.value} value={s.value}>{s.label}</ToggleGroupItem>)}
                            </ToggleGroup>
                        </FieldSet>

                        {(w.type === 'bar' || w.type === 'pie') && (
                            <Field>
                                <FieldLabel>Sort</FieldLabel>
                                <Pick label="Sort" value={w.sort ?? 'value_desc'} options={SORTS} onChange={(sort) => patch({ sort })} />
                            </Field>
                        )}
                        {w.type !== 'kpi' && (
                            <Field>
                                <FieldLabel htmlFor="w-limit">{w.type === 'table' ? 'Row limit' : 'Maximum values'}</FieldLabel>
                                <Input id="w-limit" type="number" min={1} max={MAX_LIMIT} placeholder="50" value={w.limit ?? ''}
                                    aria-invalid={w.limit !== undefined && (w.limit < 1 || w.limit > MAX_LIMIT)}
                                    onChange={(e) => patch({ limit: e.target.value === '' ? undefined : Number(e.target.value) })} />
                                <FieldDescription>Default 50, at most {MAX_LIMIT}.</FieldDescription>
                            </Field>
                        )}
                    </FieldGroup>

                    <div className="flex flex-col gap-3 lg:sticky lg:top-0 lg:self-start">
                        <h3 className="text-sm font-medium">Live preview</h3>
                        {hints.length > 0 ? (
                            <Alert>
                                <AlertTitle>Almost there</AlertTitle>
                                <AlertDescription><ul className="list-disc pl-4">{hints.map((h) => <li key={h}>{h}</li>)}</ul></AlertDescription>
                            </Alert>
                        ) : preview.error ? (
                            <ErrorState message={preview.error} problems={preview.problems} />
                        ) : (
                            <WidgetTile
                                widget={spec}
                                slot={{ loading: preview.loading || (!preview.result && ready), result: preview.result, at: Date.now() }}
                                onRefresh={() => setW((cur) => ({ ...cur }))}
                            />
                        )}
                    </div>
                </div>
                <SheetFooter className="flex-row justify-end gap-2 border-t">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={save} disabled={hints.length > 0}>{initial ? 'Apply changes' : 'Add widget'}</Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    )
}
